import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { BaseDataLayerKey, BaseDataOption } from "fond/project/panels/ExtendedDesign/BaseData/layers";
import { Store } from "fond/types";

/** What the Input Data map flyout shows: a base data row, or the Modify data tools. */
export type BaseDataFlyoutTarget = BaseDataLayerKey | "modifyData";

export type BaseDataFlyoutState = {
  /** What the flyout shows, or null when it is closed. */
  target: BaseDataFlyoutTarget | null;
  /** The row option expanded in the flyout, if any. */
  expanded: BaseDataOption | null;
};

const initialState: BaseDataFlyoutState = { target: null, expanded: null };

/**
 * Redux slice storing what the Input Data map flyout shows, so screens stacked over it can reopen it.
 */
export const baseDataFlyoutSlice = createSlice({
  name: "baseDataFlyout",
  initialState,
  reducers: {
    /** Show a target in the flyout (or close it with null), optionally with an option expanded. */
    openBaseDataFlyout: (
      state,
      action: PayloadAction<{ target: BaseDataFlyoutTarget | null; expanded?: BaseDataOption | null }>
    ): BaseDataFlyoutState => ({ target: action.payload.target, expanded: action.payload.expanded ?? null }),
    closeBaseDataFlyout: (): BaseDataFlyoutState => initialState,
    setBaseDataFlyoutExpanded: (state, action: PayloadAction<BaseDataOption | null>): BaseDataFlyoutState => ({
      ...state,
      expanded: action.payload,
    }),
  },
});

export const { openBaseDataFlyout, closeBaseDataFlyout, setBaseDataFlyoutExpanded } = baseDataFlyoutSlice.actions;

/** What the Input Data map flyout shows, or null when it is closed. */
export const selectBaseDataFlyoutTarget = (state: Store): BaseDataFlyoutTarget | null => state.baseDataFlyout.target;

/** The row option expanded in the Input Data map flyout, if any. */
export const selectBaseDataFlyoutExpanded = (state: Store): BaseDataOption | null => state.baseDataFlyout.expanded;
